import React, { useEffect } from 'react';
import TopBar from 'components/TopBar/TopBar';
import Copyright from 'components/Copyright/Copyright';
import Button from 'components/Button/Button';
import routes from 'routes/routes';
import FEATURE_GROUPS from 'content/features';
import {
	Section,
	SectionInner,
	SectionBody,
	SectionHeader,
	SectionEyebrow,
	SectionTitle,
	SectionSubtitle,
	Grid,
	Item,
	ItemTitle,
	ItemText,
	PlatformNote,
} from 'components/Section/styled';
import {
	Wrapper,
	Intro,
	IntroEyebrow,
	IntroTitle,
	IntroLead,
	Closing,
	ClosingTitle,
	ClosingText,
} from './styled';

/**
 * Reasons-to-choose copy. Like the landing page, every claim here maps to
 * capability that exists in flass-backend / flass-frontend / flass-native —
 * keep it that way, and put anything unshipped on a roadmap instead.
 */
const REASONS = [
	{
		eyebrow: 'The problem',
		title: 'Campus communication is scattered across tools nobody agreed on',
		subtitle:
			'Announcements go out by email, coursework questions end up in a consumer chat app, office hours are booked through a spreadsheet, and society life happens somewhere the university cannot see at all.',
		features: [
			{
				title: 'Students miss things',
				text:
					'A deadline posted to email, a clarification posted to a group chat, and a room change posted to the VLE means three places to check and one of them will be missed.',
			},
			{
				title: 'Staff repeat themselves',
				text:
					'The same question gets asked in a dozen private messages because there was never one obvious place to ask it where everyone could see the answer.',
			},
			{
				title: 'The institution loses sight',
				text:
					'When coursework discussion lives in an external group chat, the university has no way to moderate it, support it, or hand it over when a member of staff leaves.',
			},
		],
	},
	{
		eyebrow: 'Purpose-built',
		title: 'Designed for a university, not adapted from an office chat tool',
		subtitle:
			'Generic team messengers assume permanent employees in fixed teams. A university runs on cohorts that change every year, timetabled contact hours, and a duty of care towards students.',
		features: [
			{
				title: 'Rooms that match your structure',
				text:
					'Modules, cohorts, and societies each get a room, and an entire intake can be provisioned in one step rather than created by hand one at a time.',
			},
			{
				title: 'Rooms that end when the module does',
				text:
					'Set a room to expire at the end of term and it closes automatically, so next year starts clean instead of inheriting years of stale channels.',
			},
			{
				title: 'Office hours with a real queue',
				text:
					'Open a session, students join a line, and staff take the next person in order. No overlapping bookings and no student wondering whether they were forgotten.',
			},
			{
				title: 'Space for difficult conversations',
				text:
					'Anonymous rooms let students raise feedback, wellbeing, and peer-support topics under a consistent pseudonym rather than their own name.',
			},
			{
				title: 'Teaching tools in the room',
				text:
					'Polls, surveys, and events run inside the conversation, so checking understanding or collecting module feedback does not mean sending everyone somewhere else.',
			},
			{
				title: 'Campus life included',
				text:
					'A marketplace and jobs board keep student-to-student activity on a service the university runs, instead of an unmoderated external group.',
			},
		],
	},
	{
		eyebrow: 'Privacy',
		title: 'Private by construction, not by policy',
		subtitle:
			'Universities hold pastoral conversations, disclosures, and personal data. Flass is built so that trust does not depend on promising to behave.',
		features: [
			{
				title: 'End-to-end encrypted messages',
				text:
					'Messages are encrypted with AES-256-GCM under per-room keys exchanged with RSA and unwrapped only on a signed-in device. Message contents are not readable on the server.',
			},
			{
				title: 'Search without exposing content',
				text:
					'Message search runs over encrypted keyword tokens rather than readable text, so students can find an old message without that message being legible in storage.',
			},
			{
				title: 'Encrypted notification previews',
				text:
					'The preview on a lock screen is encrypted in transit too, so a private message stays private on the way to the device.',
			},
			{
				title: 'Accounts you already control',
				text:
					'Sign-in uses university email, so access follows the same joiner and leaver process as every other institutional system.',
			},
		],
	},
	{
		eyebrow: 'Adoption',
		title: 'The rollout is the part that usually fails',
		subtitle:
			'A communication tool only works if the whole cohort actually uses it. Flass is built to remove the reasons people do not.',
		features: [
			{
				title: 'No new account to create',
				text:
					'Students sign in with the university email they already have. There is no invite code to chase and no separate password to reset.',
			},
			{
				title: 'On the devices students use',
				text:
					'Native iOS and Android apps alongside the web app, so a question asked in a lecture is answered from a phone on the bus home.',
			},
			{
				title: 'Notifications people keep switched on',
				text:
					'Per-room notification settings mean a student can stay reachable for their tutor without being woken by a society group at 2am — so they do not mute everything.',
			},
			{
				title: 'One place, not five',
				text:
					'Messaging, office hours, events, files, polls, and surveys are the same product. Adopting Flass replaces tools rather than adding one more.',
			},
		],
	},
];

const WhyFlass = (props) => {
	useEffect(() => {
		document.title = 'Why Flass? | Flass';
	});

	return (
		<Wrapper>
			<TopBar />
				<Intro>
					<IntroEyebrow>Why Flass</IntroEyebrow>
					<IntroTitle>
						One place your students will actually check.
					</IntroTitle>
					<IntroLead>
						Flass replaces the patchwork of email threads, consumer
						group chats, and booking spreadsheets that campus
						communication usually runs on — with something built for
						how universities work, and private enough for the
						conversations they have.
					</IntroLead>
				</Intro>

			{REASONS.map((group, groupIndex) => (
				<Section key={group.eyebrow} $alt={groupIndex % 2 === 1}>
					<SectionInner>
						<SectionBody>
							<SectionHeader>
								<SectionEyebrow>{group.eyebrow}</SectionEyebrow>
								<SectionTitle>{group.title}</SectionTitle>
								<SectionSubtitle>
									{group.subtitle}
								</SectionSubtitle>
							</SectionHeader>
							<Grid>
								{group.features.map((feature, i) => (
									<Item key={feature.title}>
											<ItemTitle>
												{feature.title}
											</ItemTitle>
										<ItemText>{feature.text}</ItemText>
									</Item>
								))}
							</Grid>
						</SectionBody>
					</SectionInner>
				</Section>
			))}

			{FEATURE_GROUPS.map((group, i) => (
				<Section
					key={group.eyebrow}
					$alt={(REASONS.length + i) % 2 === 1}>
					<SectionInner>
						<SectionBody>
							<SectionHeader>
								<SectionEyebrow>{group.eyebrow}</SectionEyebrow>
								<SectionTitle>{group.title}</SectionTitle>
								<SectionSubtitle>
									{group.subtitle}
								</SectionSubtitle>
							</SectionHeader>
							<Grid>
								{group.features.map((feature) => (
									<Item key={feature.title}>
										<ItemTitle>{feature.title}</ItemTitle>
										<ItemText>{feature.text}</ItemText>
									</Item>
								))}
							</Grid>
							{i === FEATURE_GROUPS.length - 1 && (
								<PlatformNote>
									Runs in the browser and as native iOS and
									Android apps — a question asked in a lecture
									is answered from a phone on the way home.
								</PlatformNote>
							)}
						</SectionBody>
					</SectionInner>
				</Section>
			))}

			<Closing>
					<ClosingTitle>Bring Flass to your university</ClosingTitle>
					<ClosingText>
						Tell us about your institution and we will show you how
						Flass would fit alongside what you already run.
					</ClosingText>
					<Button
						onClick={() => props.history.push(routes.HOME)}
						size='medium'
						type='secondary'>
						Get in touch
					</Button>
			</Closing>

			<Copyright />
		</Wrapper>
	);
};

export default WhyFlass;
